<template>
  <div flex font-mono text-sm text-stone-500>
    <p v-if="pending && !source" h-5 w-64 max-w-full bg-stone-200 bg-opacity-60 rounded animate-pulse></p>
    <Popover v-else-if="source" relative>
      <PopoverButton
        class="group inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-left rounded outline-none focus-visible:(ring-2 ring-orange-300)"
      >
        <span v-if="detail" inline-flex items-center gap-2 whitespace-nowrap text-stone-700>
          <span inline-flex items-end gap-0.5 h-3 aria-hidden="true">
            <span
              v-for="bar in 3"
              :key="bar"
              w-1
              rounded-sm
              :class="bar <= detail.bars ? detail.barClass : 'bg-stone-300'"
              :style="{ height: `${bar * 33.3}%` }"
            ></span>
          </span>
          {{ detail.label }}
        </span>
        <span whitespace-nowrap text-stone-400 group-hover:text-stone-600>
          from {{ source.producer }}
          <span class="i-material-symbols-info-outline-rounded inline-block align--0.2em"></span>
        </span>
      </PopoverButton>
      <transition
        enter-active-class="transition duration-150 ease-out"
        enter-from-class="translate-y-1 opacity-0"
        enter-to-class="translate-y-0 opacity-100"
      >
        <PopoverPanel
          class="absolute left-0 top-full z-20 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-3 border-2 border-stone-100 bg-white p-4 font-sans text-base text-stone-600 shadow-lg space-y-3"
        >
          <div>
            <p font-mono text-xs uppercase tracking-wide text-stone-400>Elevation data</p>
            <p text-lg font-semibold text-stone-800 lh-snug>{{ source.label }}</p>
            <p text-sm>{{ [source.producer, source.region].filter(Boolean).join(" · ") }}</p>
          </div>
          <dl grid grid-cols-2 gap-x-4 gap-y-1 text-sm>
            <template v-if="resolution">
              <dt text-stone-400>Grid spacing</dt>
              <dd tabular-nums>{{ resolution }}</dd>
            </template>
            <template v-if="method">
              <dt text-stone-400>Measured by</dt>
              <dd>{{ method.label }}</dd>
            </template>
            <dt text-stone-400>License</dt>
            <dd>
              <a :href="source.licenseUrl" target="_blank" rel="noopener" underline hover:text-stone-400>
                {{ shortLicense }}
              </a>
            </dd>
          </dl>
          <p v-if="source.method === 'satellite'" text-sm text-stone-500>
            Satellite data measures the top of buildings and trees, so readings in cities and forests can run high.
          </p>
          <p flex flex-wrap gap-x-4 gap-y-1 text-sm>
            <a
              :href="source.website"
              target="_blank"
              rel="noopener"
              class="inline-flex items-center gap-1 text-blue-600 hover:text-blue-400"
            >
              View dataset
              <span class="i-material-symbols-arrow-outward-rounded inline-block"></span>
            </a>
            <a href="#elevation-data" class="inline-flex items-center gap-1 text-blue-600 hover:text-blue-400">
              About this data
              <span class="i-material-symbols-arrow-downward-rounded inline-block"></span>
            </a>
          </p>
        </PopoverPanel>
      </transition>
    </Popover>
  </div>
</template>
<script setup>
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";

const props = defineProps({
  // sources covering the location, finest resolution first
  sources: {
    type: Array,
    default: () => [],
  },
  pending: Boolean,
  elevationUnits: {
    type: Object,
    required: true,
  },
});

// Mapterhorn prefers the most detailed source, so the first one is where the reading came from
const source = computed(() => props.sources?.[0]);
const detail = computed(() => elevationDetailLevel(source.value?.resolution));
const resolution = computed(() => formatResolution(source.value?.resolution, props.elevationUnits));
const method = computed(() => ELEVATION_METHODS[source.value?.method]);
// some license names include the licensor, e.g. "CC-BY-4.0: Land Kärnten - data.gv.at"
const shortLicense = computed(() => source.value?.license?.split(/:\s/)[0]);
</script>
